import { useMemo } from "react";
import { formatPromptCount } from "@/utils/ai/utils";
import { useSubscriptionInfo } from "./useSubscriptionInfo";
import { useUsageLimits } from "./useUsageLimits";

export function useUsageMessage() {
  const { usingPrompts } = useSubscriptionInfo();
  const { remainingUsage } = useUsageLimits();

  const usageMessage = useMemo(() => {
    if (Object.keys(remainingUsage).length === 0) return "";

    if (remainingUsage.hasPromptsLeft === false) {
      return "No credits available.";
    }

    // new plans
    if (usingPrompts) {
      const combinedPrompts =
        Math.min(
          remainingUsage.dailyPromptsLeft === undefined
            ? Number.POSITIVE_INFINITY
            : remainingUsage.dailyPromptsLeft,
          remainingUsage.monthlyPromptsLeft === undefined
            ? Number.POSITIVE_INFINITY
            : remainingUsage.monthlyPromptsLeft,
        ) + (remainingUsage.extraPromptsInPackages || 0);

      // If there are more than 10 credits in packages, don't show anything
      if (combinedPrompts > 10) {
        return "";
      }

      // If there are prompts/credits in packages
      if (
        remainingUsage.extraPromptsInPackages !== undefined &&
        remainingUsage.extraPromptsInPackages > 0
      ) {
        return `${formatPromptCount(combinedPrompts)} combined credit(s) left`;
      }

      // If there are no credit packages, show the lower of daily or monthly credits
      const dailyPromptsLeft = remainingUsage.dailyPromptsLeft;
      const monthlyPromptsLeft = remainingUsage.monthlyPromptsLeft;

      if (dailyPromptsLeft !== undefined && monthlyPromptsLeft !== undefined) {
        if (monthlyPromptsLeft <= dailyPromptsLeft) {
          return `${formatPromptCount(monthlyPromptsLeft)} credits left this month`;
        }
        return `${formatPromptCount(dailyPromptsLeft)} credits left today`;
      }
      if (monthlyPromptsLeft !== undefined) {
        return `${formatPromptCount(monthlyPromptsLeft)} credits left this month`;
      }
    } else {
      // old plan
      if (
        remainingUsage.tokensLeft !== undefined &&
        remainingUsage.tokensLeft < 500000
      ) {
        return `${Math.floor(remainingUsage.tokensLeft / 1000)}k tokens left`;
      }
    }

    return "";
  }, [remainingUsage, usingPrompts]);

  return {
    usageMessage,
  };
}
